// Credit-gated action bar for an RLP. Renders the Extract button on
// 'not_started' / 'failed' RLPs and Find-Matches on 'extracted'. Surfaces
// pricing from /api/credits/pricing so the credit cost label stays in sync
// with the DB-backed pricing_config table.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Search, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { ApiError } from '@/lib/api';

function creditCost(data: Awaited<ReturnType<typeof api.creditsPricing>> | undefined, key: string): number | null {
  return data?.pricing.find((p) => p.action_key === key)?.credit_cost ?? null;
}

function parseApiErrorExtra(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof ApiError)) return null;
  try {
    return JSON.parse(err.detail) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function RlpActionBar({
  rlpId,
  extractionStatus,
}: {
  rlpId: string;
  extractionStatus: 'not_started' | 'pending' | 'extracted' | 'failed';
}) {
  const qc = useQueryClient();
  const pricing = useQuery({
    queryKey: ['credits-pricing'],
    queryFn: api.creditsPricing,
    staleTime: 5 * 60 * 1000,
  });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 30_000 });

  const extract = useMutation({
    mutationFn: () => api.extractRlp(rlpId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rlp', rlpId] });
      qc.invalidateQueries({ queryKey: ['rlps'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['credits'] });
    },
  });

  const match = useMutation({
    mutationFn: () => api.matchRlp(rlpId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rlp', rlpId] });
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['credits'] });
    },
  });

  const extractCost = creditCost(pricing.data, 'rlp_extraction');
  const matchCost = creditCost(pricing.data, 'marketplace_scan');
  const exempt = me.data?.workspace.billing_exempt ?? false;

  const showExtract =
    extractionStatus === 'not_started' || extractionStatus === 'failed';
  const showMatch = extractionStatus === 'extracted';
  const showPending = extractionStatus === 'pending';

  const activeError = extract.isError ? extract.error : match.isError ? match.error : null;
  const errExtra = parseApiErrorExtra(activeError);
  const insufficient =
    activeError instanceof ApiError && activeError.status === 402;

  return (
    <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-50 text-brand-700">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[0.9375rem] font-semibold text-ink">
            {showExtract && 'Extract requirements'}
            {showMatch && 'Find candidate properties'}
            {showPending && 'Extraction in progress'}
          </div>
          <div className="text-[0.75rem] text-ink-muted">
            {showExtract &&
              (exempt
                ? 'Internal team — billing exempt.'
                : `Opus parses the PDF, delineated area, security level, parking.`)}
            {showMatch &&
              (exempt
                ? 'Internal team — billing exempt.'
                : 'Crexi + LoopNet scan, geofence, scoring. Publishes to outreach queue.')}
            {showPending && 'Check back in ~90 seconds.'}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end gap-1">
        {showExtract && (
          <Button
            onClick={() => extract.mutate()}
            disabled={extract.isPending || pricing.isLoading}
            data-testid="action-extract"
          >
            {extract.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Extract{extractCost != null ? ` — ${extractCost} cr` : ''}
          </Button>
        )}
        {showMatch && (
          <Button
            onClick={() => match.mutate()}
            disabled={match.isPending || pricing.isLoading}
            data-testid="action-match"
          >
            {match.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            Find matches{matchCost != null ? ` — ${matchCost} cr` : ''}
          </Button>
        )}
        {showPending && (
          <div className="inline-flex items-center gap-1.5 text-[0.8125rem] text-ink-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Processing
          </div>
        )}
        {activeError && (
          <div
            role="alert"
            className="max-w-xs text-right text-[0.75rem] font-medium text-danger-bold"
          >
            {insufficient ? (
              <>
                Insufficient credits
                {typeof errExtra?.balance === 'number' && typeof errExtra?.credit_cost === 'number'
                  ? ` (${errExtra.balance} / ${errExtra.credit_cost} needed)`
                  : ''}
                {' · '}
                <Link to="/settings/billing" className="underline">
                  top up
                </Link>
              </>
            ) : (
              activeError.message ?? 'Action failed'
            )}
          </div>
        )}
      </div>
    </div>
  );
}
